import { createSignal, createEffect, createMemo, onMount } from "solid-js";
import * as d3 from "d3";

import { selectedEvents, setSelectedEvents, setLegendSelectionActive, triggerUpdate, setTriggerSelection, setTriggerUpdate, setHasSelection, setSelection, isEventHidden, selectedRange, legendSelectionActive, highlightedEventIds } from "../../../store/selectionStore";
import { phase, color, tabledata, setTableData, setFiltered, eventType, maneuverTableSort, setManeuverTableSort, resetManeuverTableSort } from "../../../store/globalStore";
import { sortManeuverRows, getManeuverSortValue } from "../../../utils/maneuverTableSort";

import { getManeuversConfig } from "../../../utils/maneuversConfig";
import { buildColorGrouping } from "../../../utils/colorGrouping";
import { resolveSelectedRowBackgroundColor } from "../../../utils/selectionColors";
import { chromaticCellHaloClass } from "../../../utils/chromaticTextReadability";
import { tws } from "../../../store/globalStore";
import { maneuverBaselineStore } from "../../../store/maneuverBaselineStore";
import { twsBinValue } from "../../../utils/maneuverBaseline";
import {
  withManeuverBaselineLossColumns,
  applyManeuverBaselineLoss,
  baselineCoversPhaseLosses,
  buildManeuverBaselineTableRow,
  prependManeuverBaselineRow,
  isManeuverBaselineRow,
} from "../../../utils/maneuverBaselineLossColumns";

export default function DataTable_Small(props: { context?: string; useSelectionIndexColors?: boolean }) {
  // Get context from props, default to 'dataset' for backward compatibility
  const context = props?.context || 'dataset';
  const useSelectionIndexColors = props?.useSelectionIndexColors === true;
  const config = getManeuversConfig(context);
  const [columns, setColumns] = createSignal<string[]>([]);
  const [descriptions, setDescriptions] = createSignal<string[]>([]);
  const [channels, setChannels] = createSignal<string[]>([]);
  const [formattedColumns, setFormattedColumns] = createSignal<string[]>([]);
  const [columnScales, setColumnScales] = createSignal({});

  // Create color scales for each categorical column type
  const getColorScaleForType = (colorType) => {
    const data = tabledata();
    if (data.length === 0) return null;
    try {
      const grouping = buildColorGrouping(data, colorType);
      return grouping.getItemColor;
    } catch (e) {
      return null;
    }
  };

  // Helper function to get color for a value in a specific column
  const getColorForValue = (value, columnType) => {
    if (!value || !columnType) return null;
    
    const scale = getColorScaleForType(columnType);
    if (!scale) return null;
    
    // Create a dummy item to get the color
    let dummyItem = {};
    if (columnType === 'SOURCE') {
      dummyItem = { source_name: value };
    } else if (columnType === 'RACE') {
      dummyItem = { Race_number: value, race: value };
    } else if (columnType === 'STATE') {
      dummyItem = { State: value, state: value, STATE: value };
    } else if (columnType === 'CONFIG') {
      dummyItem = { Config: value, config: value, CONFIG: value };
    } else if (columnType === 'CREW') {
      dummyItem = { Crew: value, crew: value, CREW: value, CREW_CODE: value };
    } else if (columnType === 'TESTS') {
      dummyItem = { Tests: value, tests: value, TESTS: value, TEST: value };
    } else if (columnType === 'YEAR') {
      dummyItem = { Year: value, year: value, YEAR: value };
    } else if (columnType === 'EVENT') {
      dummyItem = { Event: value, event: value, EVENT: value };
    }
    
    try {
      return scale(dummyItem);
    } catch (e) {
      return null;
    }
  };

  /** The baseline reference for the current selection, or null when none applies. */
  const baselineContext = () => {
    const series = maneuverBaselineStore.baselineActive() ? maneuverBaselineStore.baselineSeries() : null;
    if (!series) { return { series: null, phaseCapable: false, bin: null as number | null }; }
    return { series, phaseCapable: baselineCoversPhaseLosses(series), bin: twsBinValue(tws()) };
  };

  const initTable = () => {
    // Use config to get table columns based on phase and color
    const bl = baselineContext();
    // While a baseline is shown the loss column reports the delta against it.
    const columnConfig = withManeuverBaselineLossColumns(
      config.getTableColumns(phase(), color(), eventType()),
      { active: !!bl.series, phaseCapable: bl.phaseCapable }
    );
    const cols = columnConfig.columns;
    const descs = columnConfig.descriptions;
    const chans = columnConfig.channels;
    setColumns(cols);
    setDescriptions(descs);
    setChannels(chans);

    // On the rows themselves, so sort and formatting need no knowledge of baselines.
    applyManeuverBaselineLoss(tabledata(), bl.series, { phaseCapable: bl.phaseCapable });

    const evt = (eventType() || '').toUpperCase();
    const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
    if (usePortStbd) {
      tabledata().forEach((item: any) => {
        const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
        item.tack = Number(twa) > 0 ? 'STBD' : 'PORT';
      });
    } else if (phase() == 'FULL' || phase() == 'TURN') {
      tabledata().forEach((item) => {
        item.tack = item.twa_entry > 0 ? 'S - P' : 'P - S';
      });
    } else if (phase() == 'INVESTMENT') {
      tabledata().forEach((item) => {
        item.tack = item.twa_entry > 0 ? 'STBD' : 'PORT';
      });
    } else {
      tabledata().forEach((item) => {
        item.tack = item.twa_entry > 0 ? 'PORT' : 'STBD';
      });
    }
  }

  const toggleEventSelection = (id) => {
    let newLength = 0;
    
    setLegendSelectionActive(false);
    setSelectedEvents((prev) => {
      const newArray = prev.includes(id)
        ? prev.filter((d) => d !== id)
        : [...prev, id];
      newLength = newArray.length;
      return newArray;
    });

    setTriggerSelection(true);
    // Keep hasSelection true when the other panel has a brush selection (e.g. map timeseries brushed in split view)
    const hasBrushRange = selectedRange() && selectedRange().length > 0;
    setHasSelection(newLength > 0 || !!hasBrushRange);
    if (newLength === 0 && !hasBrushRange) {
      setSelection([]);
    }
  };

  const isEventSelected = (id: number) => highlightedEventIds().includes(id);

  const sortData = (key: string) => {
    const currentSort = maneuverTableSort();
    const direction = currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc';

    setManeuverTableSort({ key, direction });

    if (key) {
      const values = tabledata().map((row) => getManeuverSortValue(row as Record<string, unknown>, key));
      const finite = values.filter((v): v is number => Number.isFinite(v));
      const min = d3.min(finite) ?? 0;
      const max = d3.max(finite) ?? 0;

      setColumnScales((prev) => ({
        ...prev,
        [key]: JSON.stringify({ domain: [min, max], range: [0, 6] }),
      }));

      setFormattedColumns((prev) => (prev.includes(key) ? prev : [...prev, key]));
    }

    const sortedData = sortManeuverRows([...tabledata()], { key, direction }, eventType());
    setTableData(sortedData);
    setFiltered(sortedData.map((r: { event_id: number }) => r.event_id));
    setTriggerUpdate(true);
  };

  const clearFormatting = () => {
    setFormattedColumns([]); // Clear all formatted columns
    setColumnScales({}); // Clear all scales

  };

  const isHeatmapChannel = (channel: string): boolean => {
    const lower = channel.toLowerCase();
    return lower !== "twa_entry" && lower !== "datetime";
  };

  const addAllColumnFormatting = (): void => {
    const keys = channels().filter(isHeatmapChannel);
    const nextScales: Record<string, string> = {};
    for (const key of keys) {
      const values = tabledata().map((row) => getManeuverSortValue(row as Record<string, unknown>, key));
      const finite = values.filter((v): v is number => Number.isFinite(v));
      const min = d3.min(finite) ?? 0;
      const max = d3.max(finite) ?? 0;
      nextScales[key] = JSON.stringify({ domain: [min, max], range: [0, 6] });
    }
    setColumnScales(nextScales);
    setFormattedColumns(keys);
    setTriggerUpdate(true);
  };

  const toggleFormatting = (): void => {
    if (formattedColumns().length === 0) {
      addAllColumnFormatting();
    } else {
      clearFormatting();
    }
  };

  createEffect(() => {
    if (triggerUpdate()) {
      // Force memo recalculation by accessing it
      renderRows();
    }
  });

  createEffect(() => {
    phase();
    color();
    eventType();
    if (tabledata().length > 0) {
      initTable();
      const ch = channels();
      const k = maneuverTableSort().key;
      if (k && ch.length > 0 && !ch.includes(k)) {
        resetManeuverTableSort();
        const sorted = sortManeuverRows([...tabledata()], { key: null, direction: 'desc' }, eventType());
        setTableData(sorted);
        setFiltered(sorted.map((r: { event_id: number }) => r.event_id));
        setTriggerUpdate(true);
      }
    }
  });

  // Watch for selectedEvents changes (from cross-window sync) and update table
  createEffect(() => {
    // Access selection + live highlight to trigger effect when either changes (for reactivity tracking)
    highlightedEventIds();
    
    // Force memo recalculation when selection changes (handles cross-window sync)
    // The memo will automatically track tabledata(), color(), phase(), etc.
    if (tabledata().length > 0) {
      renderRows();
    }
  });

  // Ensure table syncs with selection state after mount (important for ManeuverWindow)
  // This ensures the table shows the correct selection when opened in a new window
  onMount(() => {
    // Use setTimeout to allow selectionStore to sync from parent window
    setTimeout(() => {
      if (tabledata().length > 0) {
        // Force re-render to sync with current selection state
        // renderRows() is reactive, but this ensures it runs after selectionStore syncs
        renderRows();
      }
    }, 100); // Small delay to allow cross-window sync to complete
  });

  // Calculate VMG intervals (5 intervals from min to max)
  const calculateVmgIntervals = (rows) => {
    const vmgValues = rows
      .map(r => parseFloat(r.vmg_perc_avg))
      .filter(v => !isNaN(v) && v !== null && v !== undefined);
    
    if (vmgValues.length === 0) return null;
    
    const min = Math.min(...vmgValues);
    const max = Math.max(...vmgValues);
    
    // Handle edge case where all values are the same
    if (min === max) {
      return {
        min,
        max,
        intervalSize: 0,
        getInterval: () => `${min.toFixed(1)}-${max.toFixed(1)}`
      };
    }
    
    const intervalSize = (max - min) / 5;
    
    return {
      min,
      max,
      intervalSize,
      getInterval: (vmg) => {
        if (vmg < min || vmg > max) return null;
        const intervalIndex = Math.min(4, Math.floor((vmg - min) / intervalSize));
        const intervalMin = min + (intervalIndex * intervalSize);
        const intervalMax = intervalIndex === 4 ? max : min + ((intervalIndex + 1) * intervalSize);
        return `${intervalMin.toFixed(1)}-${intervalMax.toFixed(1)}`;
      }
    };
  };

  const groupKeyName = () => {
    switch (color()) {
      case 'TWS': return 'tws_bin';
      case 'VMG': return 'vmg_interval'; // Special handling for VMG intervals
      case 'TACK': return 'tack';
      case 'RACE': return 'Race_number';
      case 'SOURCE': return 'source_name';
      case 'STATE': return 'State';
      case 'CONFIG': return 'Config';
      case 'CREW': return 'Crew';
      case 'TESTS': return 'Tests';
      case 'YEAR': return 'Year';
      case 'EVENT': return 'Event';
      default: return 'tws_bin';
    }
  };

  const buildGroupedTable = (rows) => {
    // For VMG, calculate intervals first
    let vmgIntervals = null;
    if (color() === 'VMG') {
      vmgIntervals = calculateVmgIntervals(rows);
      if (!vmgIntervals) return rows; // Fallback if no valid VMG data
    }

    const gkey = groupKeyName();
    const groups = new Map();
    (rows || []).forEach(r => {
      let k;
      if (color() === 'VMG' && vmgIntervals) {
        const vmg = parseFloat(r.vmg_perc_avg);
        k = vmgIntervals.getInterval(vmg);
        if (!k) return; // Skip invalid values
      } else {
        // Handle case variations for State field
        if (gkey === 'State') {
          k = r[gkey] ?? r['state'] ?? r['STATE'];
        } else {
          k = r[gkey];
        }
      }
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(r);
    });
    const isNumeric = (v) => typeof v === 'number' && !isNaN(v) && isFinite(v);
    const exclude = new Set(['event_id','Datetime','twa_entry']);
    const averaged = [];
    groups.forEach((items, key) => {
      const out = {};
      // Datetime: earliest (support lowercase datetime from API)
      const firstDt = items[0]?.datetime ?? items[0]?.Datetime ?? items[0]?.DATETIME;
      out.Datetime = items.reduce((minVal, r) => {
        const dt = r.datetime ?? r.Datetime ?? r.DATETIME;
        return dt != null && (minVal == null || new Date(dt) < new Date(minVal)) ? dt : minVal;
      }, firstDt);
      // pick first categorical for basis
      const first = items[0] || {};
      // Representative event_id so selection from grouped row matches map/timeseries (they use first-in-group as rep)
      out.event_id = first.event_id != null ? first.event_id : undefined;
      out.twa_entry = first.twa_entry;
      out.Race_number = first.Race_number;
      out.source_name = first.source_name;
      out.State = first.State ?? first.state ?? first.STATE;
      out.Config = first.Config ?? first.config ?? first.CONFIG;
      out.Crew = first.Crew ?? first.crew ?? first.CREW ?? first.CREW_CODE;
      out.Tests = first.Tests ?? first.tests ?? first.TESTS ?? first.TEST ?? 'NONE';
      // average numeric
      const sums = {};
      const counts = {};
      items.forEach(r => {
        Object.keys(r).forEach(k => {
          if (exclude.has(k)) return;
          if (isNumeric(r[k])) {
            sums[k] = (sums[k] || 0) + r[k];
            counts[k] = (counts[k] || 0) + 1;
          } else {
            out[k] = r[k];
          }
        });
      });
      Object.keys(sums).forEach(k => { out[k] = sums[k] / counts[k]; });
      const evt = (eventType() || '').toUpperCase();
      const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
      if (usePortStbd) {
        const twa = first.twa_entry ?? first.twa_build ?? first.Twa_start ?? 0;
        out.tack = Number(twa) > 0 ? 'STBD' : 'PORT';
      } else if (phase() == 'FULL' || phase() == 'TURN') {
        out.tack = first.twa_entry > 0 ? 'S - P' : 'P - S';
      } else if (phase() == 'INVESTMENT') {
        out.tack = first.twa_entry > 0 ? 'STBD' : 'PORT';
      } else {
        out.tack = first.twa_entry > 0 ? 'PORT' : 'STBD';
      }
      // reflect grouping value to displayed field if needed for categorical color modes
      switch (color()) {
        case 'RACE': 
          // Convert -1 to 'TRAINING' when grouping by RACE
          out.Race_number = (key === -1 || key === '-1' || String(key) === '-1') ? 'TRAINING' : key;
          break;
        case 'SOURCE': out.source_name = key; break;
        case 'STATE': out.State = key; break;
        case 'CONFIG': out.Config = key; break;
        case 'CREW': out.Crew = key; break;
        case 'TESTS': out.Tests = key; break;
        case 'YEAR': out.Year = key; break;
        case 'EVENT': out.Event = key; break;
        case 'TACK': out.tack = key; break;
      }
      averaged.push(out);
    });
    return averaged;
  };

  // Make renderRows reactive to color, phase, and data changes
  const renderRows = createMemo(() => {
    initTable()
    const bl = baselineContext();
    // Render-time only: the pinned row must never reach tabledata / the scatter / selections.
    const rowsToRender = prependManeuverBaselineRow(
      tabledata().filter((row) => !isEventHidden(row.event_id)),
      buildManeuverBaselineTableRow(bl.series, bl.bin, { phaseCapable: bl.phaseCapable })
    );

    return rowsToRender.map((row) => {
      const isBaselineRow = isManeuverBaselineRow(row);
      const isSelected = !isBaselineRow && isEventSelected(row.event_id);
      const selected = highlightedEventIds();

      const rowColor = isSelected
        ? resolveSelectedRowBackgroundColor(row, {
            selectedIds: selected,
            legendSelectionActive: legendSelectionActive(),
            colorMode: color(),
            getDimensionColor: (item) => getColorScaleForType(color())?.(item as typeof row) ?? "white",
            fallback: "white",
            forceIndexColors: useSelectionIndexColors,
          })
        : "white";

      return (
        <tr
          class={[isSelected ? "row-selected" : "", isBaselineRow ? "maneuver-baseline-table-row" : ""].filter(Boolean).join(" ") || undefined}
          data-maneuver-event-id={!isBaselineRow ? String(row.event_id) : undefined}
          // The baseline row gets no inline background: unselected rows are painted white here, and
          // an inline !important outranks the stylesheet, so the row's own transparent cells would
          // let that white through under light theme text.
          style={!isBaselineRow && rowColor ? `background-color: ${rowColor} !important; color: ${isSelected ? "white !important" : "inherit"}` : undefined}
          onClick={() => { if (!isBaselineRow) { toggleEventSelection(row.event_id); } }}
        >
          {columns().map((column, colIndex) => {
            const channel = channels()[colIndex];
            let cellValue = "";
            let cellClass = "";

            switch (column) {
              case "DATETIME": {
                cellValue = row.datetimeLocal ?? "";
                cellClass = "head";
                break;
              }
              case "TACK":
                cellValue = row['tack'];
                if (cellValue == 'PORT') {
                  cellClass = "port";
                } else if (cellValue == 'STBD') {
                  cellClass = "stbd";
                } else {
                  cellClass = cellValue === "P - S" ? "port" : "stbd";
                }
                break;
              case "RACE":
                // Access the field value and convert to string
                const raceValue = row[channel] ?? row['Race_number'] ?? row['race_number'] ?? row['race'] ?? row['Race'] ?? '';
                // Convert -1 to 'TRAINING' for display
                if (raceValue === -1 || raceValue === '-1' || String(raceValue) === '-1') {
                  cellValue = 'TRAINING';
                } else {
                  cellValue = String(raceValue);
                }
                break;
              case "SOURCE":
                cellValue = row[channel];
                break;
              case "STATE":
                // Access the field value and convert to string
                cellValue = String(row[channel] ?? row['State'] ?? row['state'] ?? '');
                break;
              case "CONFIG":
                // Access the field value and convert to string
                cellValue = String(row[channel] ?? row['Config'] ?? row['config'] ?? '');
                break;
              case "CREW":
                cellValue = String(row[channel] ?? row['Crew'] ?? row['crew'] ?? row['CREW_CODE'] ?? '');
                break;
              case "TESTS":
                cellValue = String(row[channel] ?? row['Tests'] ?? row['tests'] ?? row['TEST'] ?? 'NONE');
                break;
              case "YEAR":
                // Access the field value and convert to string
                cellValue = String(row[channel] ?? row['Year'] ?? row['year'] ?? '');
                break;
              case "EVENT":
                // Access the field value and convert to string
                cellValue = String(row[channel] ?? row['Event'] ?? row['event'] ?? '');
                break;
              case "LOSS":
                cellValue = (Math.round((row[channel] ?? 0) * 10) / 10).toString();
                break;
              default: {
                // A missing value must print blank, not "NaN": Exit_time / Bsp_exit are NULL on a
                // takeoff where no pop sample was found, and Pop_time on one that never reached
                // 32 KPH (which is also why that takeoff is graded 1).
                const raw = row[channel];
                const num = Number(raw);
                if (raw === null || raw === undefined || raw === "") {
                  cellValue = "";
                } else if (Number.isFinite(num)) {
                  cellValue = (Math.round(num * 10) / 10).toString();
                } else {
                  cellValue = String(raw);
                }
                break;
              }
            }

            // Apply conditional formatting for all previously sorted columns. The pinned baseline
            // row is the reference the heat scale is read against, so it stays unshaded.
            if (!isBaselineRow && channel !== "Twa_entry" && channel !== "Datetime") {
              if (
                formattedColumns().includes(channel) &&
                columnScales()[channel]
              ) {
                const { domain, range } = JSON.parse(
                  columnScales()[channel]
                ); // Deserialize scale
                const scale = d3.scaleLinear().domain(domain).range(range); // Recreate D3 scale
                const scaleValue = scale(
                  channel === "Datetime"
                    ? new Date(row[channel]).getTime()
                    : row[channel]
                );
                cellClass = `c${Math.round(scaleValue)}`;
              }
            }

            // Get color for categorical columns (SOURCE, MAINSAIL, HEADSAIL, RACE, STATE, CONFIG, YEAR, EVENT)
            let cellStyle = {};
            let chromaHalo = "";
            if (column === 'SOURCE' || column === 'MAINSAIL' || column === 'HEADSAIL' || column === 'RACE' || column === 'STATE' || column === 'CONFIG' || column === 'CREW' || column === 'TESTS' || column === 'YEAR' || column === 'EVENT') {
              const cellColor = getColorForValue(cellValue, column);
              if (cellColor) {
                cellStyle = { color: cellColor, fontWeight: 'bold' };
              }
              chromaHalo = chromaticCellHaloClass(cellColor ?? null, {
                sourceLabel: column === 'SOURCE' ? String(cellValue) : undefined,
              });
            }

            // Check if this is DATETIME column and grade is 4, add golden star
            const grade = row.grade ?? row.Grade ?? row.GRADE;
            const isGrade4 = column === 'DATETIME' && grade !== undefined && grade !== null && Number(grade) === 4;
            const cellContent = isGrade4 ? (
              <>
                <span style={{ color: '#FFD700' }}>★</span>  {cellValue}
              </>
            ) : cellValue;

            return <td class={`centered ${cellClass}${chromaHalo}`} style={cellStyle}>{cellContent}</td>;
          })}
        </tr>
      );
    });
  });

  return (
    <div style={{ display: 'flex', 'flex-direction': 'column', height: '100%', 'min-height': 0 }}>
      <div class="scrollable-container" style={{ flex: '1 1 auto', 'min-height': 0 }}>
        <table
          class="maneuvers-table"
          classList={{ "maneuvers-table--formatted": formattedColumns().length > 0 }}
        >
          <thead>
            <tr>
              {columns().map((column, index) => (
                <th
                  title={descriptions()[index]}
                  onClick={() => sortData(channels()[index])}
                  style={{ cursor: 'pointer' }}
                >
                  {column} {maneuverTableSort().key === channels()[index] ? (maneuverTableSort().direction === 'asc' ? '▲' : '▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRows()}</tbody>
        </table>
      </div>
      <div class="flex justify-end mt-2">
        <button class="btn" type="button" onClick={toggleFormatting}>
          {formattedColumns().length > 0 ? "Clear Formatting" : "Add formatting"}
        </button>
      </div>
    </div>
  );
}
